import type { ApiResponse } from '@bower/shared';
import { auth } from '../config/firebase';
import type { WorkspaceItemsPage } from '../services/api/workspaceItems.api';
import { queryKeys, type WorkspaceItemsListParams } from './queryClient';

/**
 * The one boot-time page-1 response (`bootItemsPrefetch.ts`), waiting OUTSIDE
 * the query cache for the page's own first fetch to take it. Outside, because
 * `WorkspaceContext` clears the whole cache on every auth transition, the boot
 * one included, so nothing a prior user loaded can survive a sign-in; a
 * prefetch written into the cache would be wiped right before the page reads
 * it.
 *
 * Taken at most once, and only by the same Firebase user asking for exactly
 * the list it was started for, within `MAX_AGE_MS`. A different list (a hover
 * prefetch of another folder) leaves it for the page; a different user or an
 * expired slot empties it. A fetch that takes it and is then aborted (an
 * unmount, React's StrictMode remount in dev) puts it back for a retry that
 * comes within `RESTORED_MAX_AGE_MS`:
 * TanStack discards an aborted fetch's result, so without that the retry would
 * ask the server again, which is the round trip this exists to save.
 */

const MAX_AGE_MS = 30_000;
/**
 * How long a put-back answer may be taken after its fetch was aborted. A remount
 * retries within milliseconds; anything later may follow a change to the list
 * (an item created or moved while the user was elsewhere), so it asks afresh.
 */
const RESTORED_MAX_AGE_MS = 1_000;

interface Slot {
  readonly uid: string;
  readonly key: string;
  readonly startedAt: number;
  /** Set when an aborted fetch put it back. */
  readonly restoredAt?: number;
  readonly response: Promise<ApiResponse<WorkspaceItemsPage> | null>;
}

let slot: Slot | null = null;

export const bootListKey = (workspaceId: string, params: WorkspaceItemsListParams): string =>
  JSON.stringify(queryKeys.workspaceItems.list(workspaceId, params));

export function putBootItemsPage(
  uid: string,
  key: string,
  response: Promise<ApiResponse<WorkspaceItemsPage> | null>,
): void {
  slot = { uid, key, startedAt: Date.now(), response };
}

/** The boot response for this list, or null to fetch as usual. */
export function takeBootItemsPage(
  workspaceId: string,
  params: WorkspaceItemsListParams,
  signal: AbortSignal,
): Promise<ApiResponse<WorkspaceItemsPage> | null> | null {
  if (!slot) return null;
  const now = Date.now();
  const expired =
    now - slot.startedAt > MAX_AGE_MS ||
    (slot.restoredAt !== undefined && now - slot.restoredAt > RESTORED_MAX_AGE_MS);
  if (expired || auth.currentUser?.uid !== slot.uid) {
    slot = null;
    return null;
  }
  if (slot.key !== bootListKey(workspaceId, params)) return null;
  const taken = slot;
  slot = null;
  signal.addEventListener(
    'abort',
    () => {
      slot ??= { ...taken, restoredAt: Date.now() };
    },
    { once: true },
  );
  return taken.response;
}

/** @internal Test seam: clears the module-scoped slot between tests. */
export function __resetBootItemsSlotForTests(): void {
  slot = null;
}
